import { t } from 'ttag'
import styled from 'styled-components'
import isEmail from 'validator/lib/isEmail'
import { useMutation } from 'react-query'
import { useMemo, useContext, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { api, errors } from '@h2/api'
import { PG } from '@h2/components/colors'
import { features } from '@h2/components/media'
import { Heading } from '@h2/components/typography/heading'
import { Text } from '@h2/components/typography/text'
import { Field } from '@h2/components/inputs/field'
import { TextInput } from '@h2/components/inputs/text'
import { userJoinedSite } from '@h2/common/actions/userJoinedSite'
import { useForm } from '@h2/common/components/hooks/useForm'
import { Alert } from '@h2/components/display/alert'
import { Card } from '@h2/common/components/card'
import { startSession } from '@h2/features/auth'
import { Globals } from '@h2/globals'

import { props } from './props'

import { Create } from '../highlights/create'
import { Controls } from '../controls'
import { Submit } from '../submit'
import { authorizationCode } from '../../../selectors/authorizationCode'

const Form = styled.form`
  display: flex;
  flex-direction: column;
  height: 100%;

  > h2 {
    font-size: 24px;
    line-height: 28px;
  }

  > fieldset {
    flex-direction: column;
  }

  @media (${features.minWidth.laptop}) {
    > h2 {
      font-size: 30px;
      line-height: 34px;
    }

    > fieldset {
      flex-direction: row;
    }
  }
`
const ParentApproval = ({ inDialog, properties, renderPanel }) => {
    const dispatch = useDispatch()
    const code = useSelector(authorizationCode)
    const { isNavianceUser, externalIdProperties } = useContext(Globals)
    const { environment, setViewMode } = useContext(Globals)
    const [errorMsg, setErrorMsg] = useState('')
    const isExternalIdRequired = useMemo(
        () => externalIdProperties?.required && !properties.externalId,
        [externalIdProperties, properties.externalId]
    )
    const { register, error, handleOnSubmit, value: formValues } = useForm(
        {
            authorizedPersonEmail(form) {
                if (!form.authorizedPersonEmail?.trim()) {
                    return t`Parent or gaurdian email is required`
                }

                if (!isEmail(form.authorizedPersonEmail)) {
                    return t`Invalid email address`
                }
            }
        }
    )
    const { mutate: joinSite, isLoading: isJoining } = useMutation(
        form =>
            api.fetch('/auth/join/respond', {
                method: 'POST',
                body: JSON.stringify({
                    code,
                    authorizedPersonEmail: form.authorizedPersonEmail,
                    externalId: form.externalId,
                    kind: 'ApprovalRequest'
                })
            }),
        {
            onSuccess: ({ user, membership }) => {
                if (!membership.removed) {
                    if (membership.accepted) {
                        dispatch(userJoinedSite(inDialog))
                        dispatch(startSession({ user, environment, setViewMode }))
                        renderPanel('complete')
                    }

                    if (membership.userCanAccept === false) {
                        renderPanel('pending', {
                            authorizedPersonEmail: formValues.authorizedPersonEmail
                        })
                    }
                }
            },
            onError: err => {
                setErrorMsg(errors.getMessage(err))
            }
        }
    )
    const isSubmitDisabled = useMemo(() => {
        return ((!formValues.authorizedPersonEmail)
        )
    }, [formValues])
    const headingTxt = `Request approval`

    return (
        <Card useDetour inDialog={inDialog}>
            <Card.Highlight color={PG}>
                {
                    <Create isNamePanel type={isNavianceUser ? 'navianceUser' : 'unknown'} />
                }
            </Card.Highlight>
            <Card.Content>
                <Form
                    id="reg-membership"
                    onSubmit={handleOnSubmit(form => joinSite(form))}
                    noValidate>
                    <Alert
                        margin="0 0 15px 0"
                        type="error"
                        isVisible={Boolean(errorMsg)}
                        message={errorMsg}
                        onClose={() => setErrorMsg('')}
                    />
                    <Heading level="2" align="center">
                        {headingTxt}
                    </Heading>
                    {isExternalIdRequired && (
                        <>
                            <Text align="center">
                                {t`Please enter a Library Card to associate with your account.`}
                            </Text>
                            <Field
                                isRequired
                                width="100%"
                                margin="15px 0 15px 0"
                                label={t`Library Card`}
                                desc={error.externalId}
                                status={error.externalId ? 'error' : 'default'}>
                                <TextInput size="small" {...register('externalId', { required: true })} />
                            </Field>
                        </>
                    )}
                    <Text align="center">
                        {t`A parent or guardian needs to approve your account so you can start using Headed2.`}
                    </Text>
                    <Field
                        isRequired
                        width="100%"
                        margin="15px 0 0 0"
                        label={t`Parent or guardian email`}
                        desc={error.authorizedPersonEmail}
                        status={error.authorizedPersonEmail ? 'error' : 'default'}>
                        <TextInput
                            size="small"
                            {...register('authorizedPersonEmail', { required: true })}
                        />
                    </Field>
                    <Controls useControls>
                        <Submit
                            text={t`Request approval`}
                            isLoading={isJoining}
                            isDisabled={isSubmitDisabled}
                        />
                    </Controls>
                </Form>
            </Card.Content>
        </Card>
    )
}

ParentApproval.propTypes = { ...props }

export { ParentApproval }